import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { ArrowLeft, Plus, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

export default function GoalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContrib, setShowContrib] = useState(false);
  const [amount, setAmount] = useState('');

  const fetchGoal = async () => {
    const res = await api.get(`/goals/${id}`);
    setGoal(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchGoal(); }, [id]);

  const handleContribute = async (e) => {
    e.preventDefault();
    await api.post(`/goals/${id}/contribute`, { amount: parseFloat(amount) });
    setShowContrib(false); setAmount('');
    fetchGoal();
  };

  if (loading || !goal) return <div className="skeleton" style={{ height: 400, borderRadius: 16 }} />;

  const pct = parseFloat(goal.progress || 0);
  const remaining = parseFloat(goal.target_amount) - parseFloat(goal.current_amount);

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/goals')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{goal.icon} {goal.name}</h1>
            <p className="page-subtitle">{goal.description}</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowContrib(true)}>
          <Plus size={18} /> Contribuir
        </button>
      </div>

      {/* Progress Card */}
      <div className="card mb-lg" style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto 20px' }}>
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r="60" stroke="var(--bg-input)" strokeWidth="10" fill="none" />
            <circle cx="70" cy="70" r="60" stroke={goal.color || 'var(--accent-blue)'} strokeWidth="10" fill="none"
              strokeDasharray={`${pct * 3.77} 377`} strokeLinecap="round"
              transform="rotate(-90 70 70)" style={{ transition: 'stroke-dasharray 0.5s' }} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span className="text-mono" style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)' }}>{pct.toFixed(0)}%</span>
          </div>
        </div>
        <div className="text-mono" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
          {formatCurrency(goal.current_amount)}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>
          de {formatCurrency(goal.target_amount)} · Faltam {formatCurrency(remaining > 0 ? remaining : 0)}
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid-3 mb-lg">
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Prazo</div>
          <div className="text-mono" style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
            {goal.deadline ? format(new Date(goal.deadline), 'd MMM yyyy', { locale: pt }) : '—'}
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Prioridade</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: { high: '#EF4444', medium: '#F59E0B', low: '#10B981' }[goal.priority] }}>
            {{ high: 'Alta', medium: 'Média', low: 'Baixa' }[goal.priority]}
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Contribuições</div>
          <div className="text-mono" style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
            {goal.contributions?.length || 0}
          </div>
        </div>
      </div>

      {/* Histórico de Contribuições */}
      <div className="card">
        <div className="section-header">
          <h3 className="section-title">Histórico de Contribuições</h3>
        </div>
        {(!goal.contributions || goal.contributions.length === 0) ? (
          <div className="empty-state">
            <p className="empty-state-title">Sem contribuições</p>
          </div>
        ) : (
          goal.contributions.map((c) => (
            <div key={c.id} className="transaction-item">
              <div className="transaction-icon" style={{ background: 'var(--accent-green-dim)' }}>
                <DollarSign size={18} color="var(--accent-green)" />
              </div>
              <div className="transaction-info">
                <div className="transaction-desc">{c.note || 'Contribuição'}</div>
                <div className="transaction-meta">
                  <span>{format(new Date(c.date), 'd MMM yyyy', { locale: pt })}</span>
                </div>
              </div>
              <div className="transaction-amount income">+{formatCurrency(c.amount)}</div>
            </div>
          ))
        )}
      </div>

      {/* Modal Contribuir */}
      {showContrib && (
        <div className="modal-overlay" onClick={() => setShowContrib(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Contribuir para {goal.name}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowContrib(false)}>×</button>
            </div>
            <form onSubmit={handleContribute}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Valor (Kz)</label>
                  <input className="form-input form-input-money" type="number" step="0.01" min="0"
                    value={amount} onChange={e => setAmount(e.target.value)} autoFocus required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowContrib(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Contribuir</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
